import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Shield, ArrowRight, CheckCircle, Users, Clock, BarChart3, 
  Send, Building2, Mail, User, MessageSquare, Phone, Globe,
  Award, Zap, Target, ChevronRight, Calendar, Linkedin,
  Scale, FileSearch, ClipboardCheck, AlertTriangle, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const navigate = useNavigate();
  const contactRef = useRef(null);
  const servicesRef = useRef(null);
  const aboutRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.company || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Thank you for your inquiry! A confirmation email has been sent to your inbox. Our team will contact you within 24 hours.');
        setFormData({ name: '', company: '', email: '', phone: '', message: '' });
      } else {
        toast.success('Thank you for your inquiry! Our partnership team will be in touch shortly.');
        setFormData({ name: '', company: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      toast.success('Thank you for your inquiry! Our partnership team will be in touch shortly.');
      setFormData({ name: '', company: '', email: '', phone: '', message: '' });
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    {
      icon: Scale,
      title: 'Financial Crimes Compliance',
      description: 'Comprehensive AML/BSA compliance programs, suspicious activity monitoring, and regulatory reporting to protect your organization.'
    },
    {
      icon: FileSearch,
      title: 'Regulatory Advisory',
      description: 'Expert guidance on navigating complex regulatory landscapes, ensuring your operations meet all federal and state requirements.'
    },
    {
      icon: ClipboardCheck,
      title: 'Internal Audits',
      description: 'Thorough internal audit programs that identify risks, strengthen controls, and prepare your organization for regulatory examinations.'
    },
    {
      icon: AlertTriangle,
      title: 'Operational Triage',
      description: 'Rapid response teams for operational challenges, process optimization, and crisis management to maintain business continuity.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A091E]" data-testid="home-page">
      {/* Navigation Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A091E]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_cadence-clock/artifacts/r3b1i9bk_srm%20cadence%20logo.jpg" 
                alt="SRM Cadence Logo" 
                className="h-12 w-auto rounded-lg"
              />
              <div className="hidden sm:block">
                <span className="text-xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                  SRM <span className="text-[#D4AF37]">Cadence</span>
                </span>
              </div>
            </Link>

            {/* Navigation - Center */}
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-white hover:text-[#D4AF37] transition-colors font-medium">Home</Link>
              <button 
                onClick={() => scrollToSection(servicesRef)} 
                className="text-white/70 hover:text-[#D4AF37] transition-colors"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection(aboutRef)} 
                className="text-white/70 hover:text-[#D4AF37] transition-colors"
              >
                About the CEO
              </button>
              <button 
                onClick={() => scrollToSection(contactRef)} 
                className="text-white/70 hover:text-[#D4AF37] transition-colors"
              >
                Contact Us
              </button>
            </nav>

            {/* Staff Portal Button - Right */}
            <Button
              onClick={() => navigate('/login')}
              className="bg-[#D4AF37] text-[#0A091E] font-bold px-6 py-2 rounded-full hover:shadow-[0_0_25px_rgba(212,175,55,0.6)] shadow-[0_0_15px_rgba(212,175,55,0.4)]"
              data-testid="btn-staff-login"
            >
              Staff Login
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1F1B71]/50 via-[#0A091E] to-[#4B167A]/30" />
          <div className="absolute top-0 left-0 w-full h-full opacity-20">
            <div className="absolute top-20 left-20 w-96 h-96 bg-[#D4AF37] rounded-full blur-[150px]" />
            <div className="absolute bottom-20 right-20 w-[500px] h-[500px] bg-[#1F1B71] rounded-full blur-[180px]" />
          </div>
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: `linear-gradient(rgba(212,175,55,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.3) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 text-center">
          <div className="animate-fade-in">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 mb-8">
              <Shield className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm font-semibold tracking-wide">Authority Through Integration</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              <span className="text-white">SRM Cadence:</span>
              <br />
              <span className="shimmer">The Operational Heartbeat</span>
              <br />
              <span className="text-white">for Global Growth</span>
            </h1>

            {/* Sub-headline */}
            <p className="text-xl sm:text-2xl text-white/70 max-w-4xl mx-auto mb-12 leading-relaxed">
              Elite BPO & Subcontracting Solutions specializing in
              <span className="text-[#D4AF37] font-semibold"> "Authority through Integration"</span>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={() => scrollToSection(contactRef)}
                className="bg-[#D4AF37] text-[#0A091E] font-bold px-10 py-5 rounded-full text-lg hover:shadow-[0_0_35px_rgba(212,175,55,0.6)] shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all"
                data-testid="btn-partner"
              >
                Partner With Us
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={() => window.open('https://calendar.app.google/RuAeHrTakS42X83MA', '_blank')}
                variant="outline"
                className="border-2 border-white/40 text-white hover:bg-white/10 hover:border-[#D4AF37] px-10 py-5 rounded-full text-lg transition-all"
                data-testid="btn-discovery"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book a Discovery Call
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-7 h-12 rounded-full border-2 border-[#D4AF37]/50 flex items-start justify-center p-2">
            <div className="w-2 h-3 bg-[#D4AF37] rounded-full" />
          </div>
        </div>
      </section>

      {/* About the CEO Section */}
      <section ref={aboutRef} id="about" className="py-24 bg-gradient-to-b from-transparent via-[#1F1B71]/10 to-transparent">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#D4AF37] text-sm font-semibold uppercase tracking-wider">Leadership</span>
            <h2 className="text-3xl sm:text-5xl font-bold text-white mt-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              About the <span className="text-[#D4AF37]">CEO</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* CEO Photo */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                {/* Photo Container with Gold Border */}
                <div className="w-72 h-72 sm:w-80 sm:h-80 rounded-full p-1 bg-gradient-to-br from-[#D4AF37] via-[#D4AF37]/60 to-[#D4AF37]">
                  <div className="w-full h-full rounded-full overflow-hidden bg-gradient-to-br from-[#1F1B71] to-[#0A091E]">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_cadence-clock/artifacts/7d4hd05m_me.jpg" 
                      alt="Sheldon R. Miller, CCSS - CEO of SRM Cadence"
                      className="w-full h-full object-cover object-top"
                      style={{ objectPosition: '50% 15%' }}
                    />
                  </div>
                </div>
                {/* Decorative Rings */}
                <div className="absolute -inset-3 rounded-full border-2 border-[#D4AF37]/30" />
                <div className="absolute -inset-6 rounded-full border border-[#D4AF37]/15" />
                {/* Glow Effect */}
                <div className="absolute -inset-8 rounded-full bg-[#D4AF37]/5 blur-xl -z-10" />
              </div>
            </div>

            {/* CEO Bio */}
            <div className="text-center lg:text-left">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Sheldon R. Miller, <span className="text-[#D4AF37]">CCSS</span>
              </h3>
              <p className="text-[#D4AF37] font-medium mb-6">Founder & Chief Executive Officer</p>
              
              <p className="text-white/70 text-lg leading-relaxed mb-8">
                Sheldon R. Miller, CCSS, is a methodical operations strategist and the Founder/CEO of SRM Cadence. 
                With over <span className="text-white font-semibold">16 years of experience</span> in financial service operations 
                and regulatory compliance, he ensures that SRM Cadence functions as a true extension of your parent company.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                <Button
                  onClick={() => scrollToSection(contactRef)}
                  className="bg-[#D4AF37] text-[#0A091E] font-bold px-6 py-3 rounded-full hover:shadow-[0_0_25px_rgba(212,175,55,0.6)]"
                  data-testid="btn-partner-ceo"
                >
                  Partner With Us
                </Button>
                <Button
                  onClick={() => window.open('https://calendar.app.google/RuAeHrTakS42X83MA', '_blank')}
                  variant="outline"
                  className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 px-6 py-3 rounded-full"
                  data-testid="btn-discovery-ceo"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book a Discovery Call
                </Button>
                <Button
                  onClick={() => window.open('https://www.linkedin.com/in/sheldon-r-miller-9a059931b', '_blank')}
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/5 px-6 py-3 rounded-full"
                  data-testid="btn-linkedin-ceo"
                >
                  <Linkedin className="w-4 h-4 mr-2" />
                  View LinkedIn Profile
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} id="services" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm font-semibold uppercase tracking-wider">Our Expertise</span>
            <h2 className="text-3xl sm:text-5xl font-bold text-white mt-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Core BPO <span className="text-[#D4AF37]">Capabilities</span>
            </h2>
            <p className="text-white/50 mt-4 max-w-2xl mx-auto text-lg">
              Specialized services designed to strengthen your operations and ensure regulatory excellence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="glass-card border-0 group hover:border-[#D4AF37]/50 transition-all duration-300 overflow-hidden">
                <CardContent className="p-8">
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#1F1B71] to-[#4B167A] flex items-center justify-center flex-shrink-0 group-hover:from-[#D4AF37]/20 group-hover:to-[#D4AF37]/10 transition-all">
                      <service.icon className="w-8 h-8 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                      <p className="text-white/60 leading-relaxed">{service.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Service Highlights */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '99.5%', label: 'Compliance Rate' },
              { value: '24/7', label: 'Operations Support' },
              { value: '16+', label: 'Years Experience' },
              { value: '100%', label: 'Client Satisfaction' }
            ].map((stat, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-[#1F1B71]/20 border border-white/5">
                <p className="text-3xl font-bold text-[#D4AF37] font-mono">{stat.value}</p>
                <p className="text-white/50 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PublicTeamSection />

      {/* Contact / Partner With Us Section */}
      <section ref={contactRef} id="contact" className="py-24 bg-gradient-to-t from-[#1F1B71]/20 to-transparent">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm font-semibold uppercase tracking-wider">Get Started</span>
            <h2 className="text-3xl sm:text-5xl font-bold text-white mt-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Partner <span className="text-[#D4AF37]">With Us</span>
            </h2>
            <p className="text-white/50 mt-4 max-w-2xl mx-auto text-lg">
              Ready to elevate your operations? Submit your inquiry and our team will respond within 24 hours.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Let's Build Together
                </h3>
                <p className="text-white/60 leading-relaxed mb-8">
                  Whether you need compliance support, operational triage, or a full BPO partnership, 
                  SRM Cadence is ready to become an extension of your team.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-[#1F1B71]/20 border border-white/5">
                  <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Email Us</p>
                    <p className="text-white font-medium">office@srm-cadence.com</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-[#1F1B71]/20 border border-white/5">
                  <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Schedule a Call</p>
                    <a 
                      href="https://calendar.app.google/RuAeHrTakS42X83MA" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#D4AF37] font-medium hover:underline flex items-center gap-1"
                    >
                      Book Discovery Call <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-xl bg-[#1F1B71]/20 border border-white/5">
                  <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                    <Linkedin className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Connect With Us</p>
                    <a 
                      href="https://www.linkedin.com/company/srm-cadence/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#D4AF37] font-medium hover:underline flex items-center gap-1"
                    >
                      SRM Cadence LinkedIn <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Inquiry Form */}
            <Card className="lg:col-span-3 glass-card border-0 border-t-4 border-t-[#D4AF37]">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-white mb-6">Partnership Inquiry Form</h3>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-white/70">Full Name *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Your full name"
                          className="pl-10 bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30 focus:border-[#D4AF37]"
                          data-testid="input-inquiry-name"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-white/70">Company *</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          placeholder="Company name"
                          className="pl-10 bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30 focus:border-[#D4AF37]"
                          data-testid="input-inquiry-company"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white/70">Business Email *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="you@company.com"
                          className="pl-10 bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30 focus:border-[#D4AF37]"
                          data-testid="input-inquiry-email"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-white/70">Phone Number</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+1 (555) 000-0000"
                          className="pl-10 bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30 focus:border-[#D4AF37]"
                          data-testid="input-inquiry-phone"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white/70">How Can We Help? *</Label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-white/30" />
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your operational needs, compliance challenges, or partnership goals..."
                        className="pl-10 bg-[#0A091E]/50 border-white/20 text-white placeholder:text-white/30 focus:border-[#D4AF37] min-h-[140px]"
                        data-testid="input-inquiry-message"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#D4AF37] text-[#0A091E] font-bold py-4 rounded-full text-lg hover:shadow-[0_0_30px_rgba(212,175,55,0.6)] shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all"
                    data-testid="btn-submit-inquiry"
                  >
                    {submitting ? 'Sending...' : 'Submit Partnership Inquiry'}
                    <Send className="w-5 h-5 ml-2" />
                  </Button>

                  <p className="text-white/40 text-xs text-center">
                    By submitting, you agree to receive a confirmation email from office@srm-cadence.com
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#0A091E]">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            {/* Logo & Tagline */}
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_cadence-clock/artifacts/r3b1i9bk_srm%20cadence%20logo.jpg" 
                alt="SRM Cadence Logo" 
                className="h-12 w-auto rounded-lg"
              />
              <div>
                <span className="text-xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                  SRM <span className="text-[#D4AF37]">Cadence</span>
                </span>
                <p className="text-white/40 text-sm">Authority Through Integration</p>
              </div>
            </div>

            {/* Footer Links */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <a 
                href="https://www.linkedin.com/company/srm-cadence/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/50 hover:text-[#D4AF37] flex items-center gap-1"
              >
                <Linkedin className="w-4 h-4" />
                LinkedIn
              </a>
              <Link to="/privacy" className="text-white/50 hover:text-[#D4AF37]">Privacy Policy</Link>
              <Link to="/terms" className="text-white/50 hover:text-[#D4AF37]">Terms of Service</Link>
              <Link to="/login" className="text-[#D4AF37] hover:text-[#D4AF37]/80 font-medium">Staff Portal</Link>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-white/5 text-center">
            <p className="text-white/30 text-sm">
              © 2026 SRM Cadence. All rights reserved. | Elite BPO & Subcontracting Solutions
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
